import React, { MouseEvent, useEffect, useState } from 'react'
import { generatePath, Link as RouterLink, useHistory, useLocation } from 'react-router-dom'
import { format, isToday } from 'date-fns'
import {
    Button,
    ChevronRightIcon,
    CopyIcon,
    Menu,
    OverflowTooltip,
    RedirectIcon,
    SecondaryVerticalIcon,
    TextIcon,
    TrashIcon,
} from '@diversioteam/diversio-ds'

import { generateCompanyURI } from 'sagas/helpers/generateCompanyURI'
import { AppRoute } from 'routing/AppRoute.enum'
import { usePulseSurveys } from 'hooks/usePulseSurveys'
import { useDeletePulseSurvey } from 'hooks/pulseSurveys/useDeletePulseSurvey'
import { useDuplicatePulseSurvey } from 'hooks/pulseSurveys/useDuplicatePulseSurvey'
import { useUpdatePulseSurvey } from 'hooks/pulseSurveys/useUpdatePulseSurvey'
import { ACTION_ALLOWED_STATUTES } from 'containers/PulseSurveys/pulseSurvey.helpers'
import { useGetPulseSurveyDetail } from 'hooks/pulseSurveys/useGetPulseSurveyDetail'
import { useGetPulseSurveys } from 'hooks/pulseSurveys/useGetPulseSurveys'

import { SurveyListItemProps } from './surveyListItem.types'
import { EditableInput } from './EditableInput'
import * as S from './surveyListItem.styles'
import { DeleteSurveyDialog } from './DeleteSurveyDialog'

export const SurveyListItem = (props: SurveyListItemProps) => {
    const history = useHistory()
    const location = useLocation()

    const { uuid, display_name: title, last_updated: lastUpdated } = props

    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

    const [isEditing, setIsEditing] = useState(false)
    const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState<null | HTMLElement>(null)

    const { activePulseSurvey, onChangeActivePulseSurvey } = usePulseSurveys()

    const { data: pulseSurveysData } = useGetPulseSurveys()

    const isActive = activePulseSurvey?.uuid === uuid

    const { data } = useGetPulseSurveyDetail(
        { surveyUUID: activePulseSurvey?.uuid || '' },
        {
            enabled: isActive,
        },
    )
    useEffect(() => {
        if (pulseSurveysData && pulseSurveysData.length === 0) {
            onChangeActivePulseSurvey(null)
            history.replace(generateCompanyURI(AppRoute.PulseSurvey))
        }
    }, [pulseSurveysData, history, location.pathname, onChangeActivePulseSurvey])

    const mutationUpdate = useUpdatePulseSurvey()
    const mutationDuplicate = useDuplicatePulseSurvey()
    const mutationDelete = useDeletePulseSurvey()

    const isMoreMenuOpen = Boolean(moreMenuAnchorEl)

    const isFocused = isMoreMenuOpen || isEditing || isActive

    const lastUpdatedDate = new Date(lastUpdated)

    const handleRenameSurvey = () => {
        setTimeout(() => {
            setIsEditing(true)
        }, 0)
    }

    const handleSave = (newTitle: string) => {
        setIsEditing(false)

        mutationUpdate.mutate({
            surveyUUID: uuid,
            body: {
                title: newTitle,
            },
        })
    }

    const handleDuplicateSurvey = () => {
        mutationDuplicate.mutate({ survey: uuid })
    }

    const handleDeleteSurvey = () => {
        setIsDeleteDialogOpen(true)
    }

    const confirmDeleteSurvey = () => {
        mutationDelete.mutate(
            { surveyUUID: uuid },
            {
                onSuccess: () => {
                    setIsDeleteDialogOpen(false)
                },
            },
        )
    }

    const cancelDeleteSurvey = () => {
        setIsDeleteDialogOpen(false)
    }

    const handleOpenInNewTab = () => {
        const newTabUrl = generateCompanyURI(generatePath(AppRoute.PulseSurveyCreate, { id: uuid }))

        window.open(newTabUrl, '_blank')
    }

    const moreMenuItems = [
        {
            icon: <TextIcon type="line" />,
            text: 'Rename',
            action: handleRenameSurvey,
        },
        {
            icon: <RedirectIcon type="line" />,
            text: 'Open in new tab',
            action: handleOpenInNewTab,
        },
        {
            icon: <CopyIcon type="line" />,
            text: 'Duplicate',
            action: handleDuplicateSurvey,
        },
        ...(data && ACTION_ALLOWED_STATUTES.delete.includes(data.status)
            ? [
                  {
                      icon: <TrashIcon type="line" />,
                      text: 'Remove',
                      action: handleDeleteSurvey,
                      isHighlighted: true,
                  },
              ]
            : []),
    ]

    const handleOpenMoreMenu = (event: MouseEvent<HTMLElement>) => {
        event.stopPropagation()
        event.preventDefault()

        setMoreMenuAnchorEl(event.currentTarget)
    }

    const handleCloseMoreMenu = (event: MouseEvent<HTMLElement>) => {
        event.stopPropagation()
        event.preventDefault()

        setMoreMenuAnchorEl(null)
    }

    return (
        <S.ListItemWrapper
            as={RouterLink}
            to={generateCompanyURI(generatePath(AppRoute.PulseSurveyCreate, { id: uuid }))}
            onClick={() => {
                onChangeActivePulseSurvey(props)
            }}
            onDoubleClick={(event) => {
                event.preventDefault()

                handleRenameSurvey()
            }}
            focused={isFocused}
        >
            {isEditing && <EditableInput initialValue={title} onSave={handleSave} />}

            {!isEditing && (
                <>
                    <S.Details>
                        <OverflowTooltip overflowDirection="height">
                            <S.ListItemLabel>{title}</S.ListItemLabel>
                        </OverflowTooltip>

                        {!isActive && (
                            <S.LastUpdated>
                                {isToday(lastUpdatedDate)
                                    ? `Opened ${format(lastUpdatedDate, 'h:mm a')}`
                                    : format(lastUpdatedDate, 'MMM do, yyyy')}
                            </S.LastUpdated>
                        )}
                    </S.Details>

                    {isActive ? (
                        <Button onClick={handleOpenMoreMenu} color="transparent" iconOnly aria-label="More options">
                            <SecondaryVerticalIcon type="line" />
                        </Button>
                    ) : (
                        <Button color="transparent" iconOnly aria-hidden={true} aria-label="">
                            <ChevronRightIcon type="line" />
                        </Button>
                    )}
                </>
            )}

            <Menu
                id="more-menu"
                anchorEl={moreMenuAnchorEl}
                open={isMoreMenuOpen}
                onClose={handleCloseMoreMenu}
                getContentAnchorEl={null}
                menuItems={moreMenuItems}
                keepMounted
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            />

            <DeleteSurveyDialog
                open={isDeleteDialogOpen}
                onClose={cancelDeleteSurvey}
                onConfirm={confirmDeleteSurvey}
                surveyTitle={title}
            />
        </S.ListItemWrapper>
    )
}
